// US2017758 Bucket C — pure locale normalizer.
//
// The Support Button's locale is derived ONLY from the host-passed `bb_lang`
// attribute, never from the browser (Hard Rule #4 forbids `navigator.language` /
// `window.location` / `document` reads). This util turns whatever token the host
// emits — `"FR"`, `"fr-ca"`, `"FR_CA"`, padded, etc. — into the canonical
// `language` or `language-REGION` form so it can be matched against the i18n
// module's `SUPPORTED_LOCALES` and passed as an explicit `lng` to i18next.
//
// Pure and synchronous — no I/O, no global reads. `fr-CA` is a first-class locale
// (its own region casing), never collapsed to `fr` here.
//
//   normalizeLocale("FR")          → "fr"
//   normalizeLocale("fr-ca")       → "fr-CA"
//   normalizeLocale("FR_CA")       → "fr-CA"
//   normalizeLocale("  en  ")      → "en"
//   normalizeLocale("")            → ""      (caller decides the default, e.g. "en")
//   normalizeLocale("fr-ca-quebec")→ "fr-CA" (primary subtag + region only)
//   normalizeLocale("english")     → ""      (not a 2–3-letter primary subtag)
//   normalizeLocale("123")         → ""      (not letters — malformed)
export function normalizeLocale(raw: string): string {
  if (!raw) return "";
  // trim → lowercase → unify the separator (`_` → `-`).
  const cleaned = raw.trim().toLowerCase().replace(/_/g, "-");
  if (!cleaned) return "";

  const [primary, region] = cleaned.split("-");
  // Leading-separator garbage (e.g. "-fr", "_ca") has no primary subtag — treat as
  // empty so the caller applies its default ("en") with no spurious warn, rather
  // than forwarding a malformed "-FR" tag.
  if (!primary) return "";
  // The primary subtag of a BCP-47 language tag is a 2–3-letter ISO-639 code. Reject
  // anything else ("english", "e", "123") as malformed → "" so the caller takes the
  // no-warn default path rather than the unrecognized-locale warn path (ARGUS PR #153
  // [Nit]). A well-formed-but-unsupported tag like "de"/"zz" still passes here and
  // correctly warns at the SUPPORTED_LOCALES membership check.
  if (!/^[a-z]{2,3}$/.test(primary)) return "";
  // Region (if present) is conventionally upper-cased: `fr-ca` → `fr-CA`.
  return region ? `${primary}-${region.toUpperCase()}` : primary;
}
